import api from './api';

// Turns phone notifications on or off for this device (see backend push.py).

const toKey = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const registration = async () => {
  const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register('/sw.js'));
  await navigator.serviceWorker.ready;
  return reg;
};

export const currentSubscription = async () => {
  if (!pushSupported()) return null;
  const reg = await registration();
  return reg.pushManager.getSubscription();
};

export const deviceState = async () => {
  const sub = await currentSubscription();
  if (!sub) return { subscribed: false, kinds: {} };
  const res = await api.post('/push/device', { endpoint: sub.endpoint });
  return res.data;
};

const isApp = () => (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
  || document.referrer.startsWith('android-app://');

// Tell the server which step failed on this phone, so it can be fixed remotely.
const report = (stage, err) => api.post('/push/client-report', {
  stage, message: String((err && (err.message || err.name)) || err || ''),
  permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  standalone: isApp(), user_agent: navigator.userAgent,
}).catch(() => {});

const withTimeout = (p, ms, stage) => Promise.race([
  p, new Promise((_, rej) => setTimeout(() => rej(new Error(`${stage} took too long`)), ms)),
]);

export const turnOn = async (kinds) => {
  let stage = 'config';
  try {
    const { data: cfg } = await api.get('/push/config');
    if (!cfg.enabled || !cfg.public_key) throw new Error('Notifications are not set up on the server yet.');
    stage = 'permission';
    const perm = await withTimeout(Notification.requestPermission(), 60000, 'The permission prompt');
    if (perm !== 'granted') {
      throw new Error(isApp()
        ? 'Notifications are blocked for the app. Open Android Settings → Apps → Furniva CRM → Notifications, turn them on, then try again.'
        : 'Notifications are blocked for this site. Allow them in the browser\'s site settings, then try again.');
    }
    stage = 'service-worker';
    const reg = await withTimeout(registration(), 15000, 'Starting the notification service');
    stage = 'subscribe';
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(cfg.public_key) }), 30000, 'Registering with Google');
    stage = 'save';
    const res = await api.post('/push/subscribe', { subscription: sub.toJSON(), kinds });
    return res.data;
  } catch (e) {
    report(stage, e.response?.data?.detail || e);
    const err = new Error(e.response?.data?.detail || e.message || 'Could not turn notifications on');
    err.stage = stage;
    throw err;
  }
};

export const turnOff = async () => {
  const sub = await currentSubscription();
  if (!sub) return;
  try { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }); } catch (e) { /* still unsubscribe locally */ }
  await sub.unsubscribe();
};

export const sendTest = () => api.post('/push/test');
